import { useCallback, useRef } from "react";
import { HoldToEarnCampaignSchema } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useUserSources } from "../user";
import { useApi } from "@/hooks/core/useApi";
import { is4xxHttpError } from "@/utils/api/is4xxHttpError";
import { getHoldCampaignWithStatus } from "@/utils/holdCampaigns";
import { useQuery } from "@tanstack/react-query";

export const useHoldCampaign = (campaignId: string | undefined) => {
  const { getSources } = useUserSources();
  const apiHelper = useApi();
  const selectCacheRef = useRef<{ raw: unknown; parsed: ReturnType<typeof getHoldCampaignWithStatus> } | null>(null);

  const selectHoldCampaign = useCallback((data: unknown) => {
    if (!data) {
      return null;
    }

    if (selectCacheRef.current?.raw === data) {
      return selectCacheRef.current.parsed;
    }

    const parsedCampaign = HoldToEarnCampaignSchema.safeParse(data);
    if (!parsedCampaign.success) {
      // eslint-disable-next-line no-console
      console.error("Invalid hold campaign data", parsedCampaign.error);
      return null;
    }

    const parsed = getHoldCampaignWithStatus(parsedCampaign.data);
    selectCacheRef.current = { raw: data, parsed };
    return parsed;
  }, []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [QueryKeys.HOLD_TO_EARN_CAMPAIGN, campaignId],
    queryFn: async () => {
      const sources = getSources();
      if (!campaignId) return null;

      const campaign = await apiHelper.getOrThrow(API_ENDPOINT.HOLD_TO_EARN_CAMPAIGN_BY_NAME, {
        queryParams: { campaignName: campaignId, utms: sources.join(",") },
      });

      return campaign;
    },
    select: selectHoldCampaign,
    enabled: !!campaignId,
    retry: (failureCount, error) => !is4xxHttpError(error) && failureCount < 3,
    refetchInterval: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000,
  });

  return {
    campaign: data ?? null,
    currentEpoch: data?.currentEpoch ?? null,
    isLoading,
    isError,
    error,
  };
};
